import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ConnectedOverlayPositionChange } from "@angular/cdk/overlay";
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, Renderer2, ViewChild } from "@angular/core";
import { LocalComponentStore, TOOLTIP_SHOW_DELAY } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, auditTime } from "rxjs";
import { IconName } from "../icon/icon.directive";

interface PopoverComponentState {
    popoverText: string;
    popoverIcon: IconName;
    isOpen: boolean;
    shouldOpenOnHover: boolean;
    hoverElement: HTMLElement | undefined;
    maxWidth: string | undefined;
}

@UntilDestroy()
@Component({
    selector: "dtm-ui-popover",
    templateUrl: "./popover.component.html",
    styleUrls: ["./popover.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class PopoverComponent implements AfterViewInit, OnDestroy {
    @ViewChild("popoverTooltip") public popoverTooltip: ElementRef<HTMLElement> | undefined;

    @Input() public set popoverText(value: string) {
        this.localStore.patchState({ popoverText: value });
    }
    @Input() public set popoverIcon(value: IconName) {
        this.localStore.patchState({ popoverIcon: value });
    }
    @Input() public set shouldOpenOnHover(value: BooleanInput) {
        this.localStore.patchState({ shouldOpenOnHover: coerceBooleanProperty(value) });
    }
    @Input() public set customHoverTriggerElement(value: HTMLElement | undefined) {
        this.handleCustomElementHover(value);
    }
    @Input() public set maxWidth(value: number) {
        this.localStore.patchState({ maxWidth: value ? `${value}px` : undefined });
    }

    private handleCustomElementHover(value: HTMLElement | undefined) {
        this.hoverElement = value;

        if (!value) {
            this.mouseOverUnlistenFn?.();

            return;
        }

        if (!this.mouseOverUnlistenFn) {
            this.mouseOverUnlistenFn = this.renderer.listen("document", "mouseover", (event: MouseEvent) => {
                this.checkHoverSubject.next(event.target as HTMLHtmlElement);
            });
        }
    }

    protected readonly popoverText$ = this.localStore.selectByKey("popoverText");
    protected readonly popoverIcon$ = this.localStore.selectByKey("popoverIcon");
    protected readonly isOpen$ = this.localStore.selectByKey("isOpen");
    protected readonly shouldOpenOnHover$ = this.localStore.selectByKey("shouldOpenOnHover");
    protected readonly maxWidth$ = this.localStore.selectByKey("maxWidth");

    private checkHoverSubject = new Subject<HTMLHtmlElement>();
    private hoverElement: HTMLElement | undefined;
    private mouseOverUnlistenFn: (() => void) | undefined;

    private isClickOpened = false;

    constructor(private readonly localStore: LocalComponentStore<PopoverComponentState>, private renderer: Renderer2) {
        this.localStore.setState({
            popoverText: "",
            popoverIcon: "information-fill",
            isOpen: false,
            shouldOpenOnHover: false,
            hoverElement: undefined,
            maxWidth: undefined,
        });
    }

    public ngOnDestroy(): void {
        this.mouseOverUnlistenFn?.();
    }

    public ngAfterViewInit(): void {
        this.checkHoverSubject.pipe(auditTime(TOOLTIP_SHOW_DELAY), untilDestroyed(this)).subscribe((target) => {
            if (this.isClickOpened) {
                return;
            }

            if (this.checkIfTargetIsPartOfAnyElement([this.hoverElement, this.popoverTooltip?.nativeElement], target)) {
                this.showPopover();
            } else {
                this.hidePopover();
            }
        });
    }

    protected clickOpen() {
        const isOpened = this.localStore.selectSnapshotByKey("isOpen");

        if (isOpened) {
            this.isClickOpened = false;
            this.hidePopover();

            return;
        }
        this.isClickOpened = true;
        this.showPopover();
    }

    protected clickClose() {
        this.isClickOpened = false;
        this.hidePopover();
    }

    protected toggleOverlay(): void {
        this.localStore.patchState((state: PopoverComponentState) => ({ isOpen: !state.isOpen }));
    }

    protected positionChange(event: ConnectedOverlayPositionChange, host: HTMLElement): void {
        this.removePointerClasses(host);

        this.renderer.addClass(host, event.connectionPair.originY);
    }

    protected removePointerClasses(host: HTMLElement): void {
        this.renderer.removeClass(host, "top");
        this.renderer.removeClass(host, "bottom");
    }

    protected hidePopover(): void {
        this.localStore.patchState({ isOpen: false });
    }

    protected showPopover(): void {
        this.localStore.patchState({ isOpen: true });
    }

    private checkIfTargetIsPartOfAnyElement(elements: (HTMLElement | undefined)[], target?: HTMLHtmlElement): boolean {
        if (!target || !elements.length) {
            return false;
        }

        return elements.some((element) => element?.contains(target));
    }
}
